import React from 'react';
import { useTheme } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import PriceVatDisclaimer from 'common/PriceVatDisclaimer';
import {
  plansData,
  getPlanTotal,
  YERALDIN_DAY_RATE,
  TNO_COVERAGE_DAY_RATE,
  PRENSA_PACKAGE_TOTAL,
} from '../data';

const planAccent = {
  bronce: '#8D6E63',
  plata: '#78909C',
  oro: '#FFB300',
};

const InversionSection = () => {
  const theme = useTheme();

  return (
    <Box>
      <Box marginBottom={4}>
        <Typography
          sx={{ textTransform: 'uppercase', fontWeight: 600 }}
          gutterBottom
          color="primary"
          align="center"
        >
          Planes de inversión
        </Typography>
        <Typography variant="h4" fontWeight={700} align="center" gutterBottom>
          Bronce, Plata y Oro
        </Typography>
        <Typography variant="body1" color="textSecondary" align="center" sx={{ maxWidth: 720, mx: 'auto' }}>
          Tres niveles de cobertura para tu evento de pádel. Cada plan incluye despliegue técnico de TNO Network
          {' '}
          (${TNO_COVERAGE_DAY_RATE}/día) y conducción en campo con Yeraldine Marrero (${YERALDIN_DAY_RATE}/día).
          El Plan Oro suma el paquete completo de contenido escrito y prensa (${PRENSA_PACKAGE_TOTAL}).
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {plansData.map((plan) => {
          const { coverage, host, prensa, total } = getPlanTotal(plan);
          const accent = planAccent[plan.id] || theme.palette.primary.main;

          return (
            <Grid item xs={12} md={4} key={plan.id} data-aos="fade-up">
              <Box
                component={Card}
                height="100%"
                display="flex"
                flexDirection="column"
                variant="outlined"
                sx={{
                  borderTop: 4,
                  borderColor: accent,
                  transition: 'all .2s ease-in-out',
                  boxShadow: plan.highlight ? 6 : 1,
                  transform: plan.highlight ? 'scale(1.02)' : 'none',
                  '&:hover': { boxShadow: 8 },
                }}
              >
                <Box
                  py={2}
                  px={2}
                  sx={{
                    bgcolor: accent,
                    color: theme.palette.common.white,
                    textAlign: 'center',
                  }}
                >
                  <Typography variant="overline" fontWeight={700} display="block">
                    {plan.tag}
                  </Typography>
                  <Typography variant="h3" fontWeight={900}>
                    ${total}
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ opacity: 0.95 }}>
                    {plan.days} {plan.days === 1 ? 'día' : 'días'} de evento
                  </Typography>
                </Box>
                <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="h6" fontWeight={700} gutterBottom>
                    {plan.title}
                  </Typography>
                  <List dense disablePadding sx={{ flex: 1, mb: 2 }}>
                    {plan.features.map((feature, idx) => (
                      <ListItem key={idx} disableGutters alignItems="flex-start">
                        <ListItemIcon sx={{ minWidth: 32, mt: 0.25 }}>
                          <CheckCircleIcon color="primary" fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                          primary={feature}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                  <Box
                    sx={{
                      mt: 'auto',
                      pt: 2,
                      borderTop: 1,
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="caption" color="textSecondary" display="block">
                      Desglose
                    </Typography>
                    <Typography variant="body2">
                      Cobertura TNO ({plan.days}×${TNO_COVERAGE_DAY_RATE}): <strong>${coverage}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Yeraldine ({plan.days}×${YERALDIN_DAY_RATE}): <strong>${host}</strong>
                    </Typography>
                    {prensa > 0 && (
                      <Typography variant="body2">
                        Paquete prensa: <strong>${prensa}</strong>
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
                    {plan.idealFor}
                  </Typography>
                </CardContent>
              </Box>
            </Grid>
          );
        })}
      </Grid>

      <Box mt={4}>
        <Box
          component={Card}
          sx={{
            bgcolor: theme.palette.grey[100],
            borderLeft: 4,
            borderColor: theme.palette.primary.main,
          }}
        >
          <CardContent>
            <Typography variant="subtitle2" color="primary" fontWeight={700} gutterBottom>
              Add-on opcional (Planes Bronce y Plata)
            </Typography>
            <Typography variant="body2" color="textSecondary">
              El paquete de contenido escrito y prensa (${PRENSA_PACKAGE_TOTAL}) puede sumarse a Bronce o Plata.
              En el Plan Oro ya está incluido. Ver detalle en la sección de contenido para patrocinadores.
            </Typography>
          </CardContent>
        </Box>
      </Box>

      <PriceVatDisclaimer />
    </Box>
  );
};

export default InversionSection;
